import { inject } from 'mobx-react';
import { commonParamBuilder } from '../../../lib/paramBuilder';
import Axios from 'axios';
import queryString from 'query-string';
import * as cookie from 'js-cookie';

export interface IStoreEvent<T> {
    data: T;
}

export interface IStoreEventHandler<T> {
    (event: IStoreEvent<T>): any;
}
export interface IStore<T> {
    getInitConfig(): T;
}

export interface IFactoryStoreConfig<T> {
    clazz: StoreClass<T>;
    config: T;
}

export interface IStoreConfig<T> {
    name: string;
    config: T;
}

// tslint:disable-next-line:interface-over-type-literal
export type StoreClass<T> = {
    new(root: RootStore, config: T): any;
};

export interface IUcApi {
    [key: string]: {
        [key: string]: ( params: {
            success: (result?: any) => void,
            fail: (error?: any) => void,
        }) => void;
    };
}

const STORE_CLASS = new Map<string, StoreClass<any>>();

export function getAllStoreClassKey() {
    return STORE_CLASS.keys();
}

export function injectStore(classes: { [key: string]: StoreClass<any> }) {
    return inject((stores: { rootStore: RootStore }) => {
        const prop = {
        };
        Object.keys(classes).forEach((key) => {
            prop[key] = stores.rootStore.getStore(classes[key][Symbol.for('store:name')]);
        });

        return prop;
    });
}

export function dataStore<T>(name: string) {
    return (clazz: StoreClass<T>): StoreClass<T> => {
        const CLAZZ = class extends clazz {
            public static get [Symbol.for('store:name')]() {
                return name;
            }
        };
        STORE_CLASS.set(name, clazz);
        return CLAZZ;
    };
}

export class CustomError<T> extends Error {
    private _data: T;
    constructor(message: string, data: T) {
        super(message);
        this._data = data;
    }
    public get data() {
        return this._data;
    }
}

export abstract class DataStore<T, S = any> implements IStore<T> {

    public readonly renderSide: 'server' | 'client';
    protected rootStore: RootStore<S>;
    protected config: T;
    constructor(rootStore: RootStore<S>, config: T) {
        this.rootStore = rootStore;
        this.config = config;
        this.renderSide = rootStore.getRootStoreConfig().renderSide;
    }
    public getInitConfig = (): T => {
        return this.config;
    }
    public getConfig = (): T => {
        return this.config;
    }
    public getRootStoreConfig = () => {
        return this.rootStore.getRootStoreConfig();
    }
    public getCommonParams = () => {
        if (this.renderSide === 'client') {
            return commonParamBuilder.getParams(queryString.parse(window.location.search));
        } else {
            return {};
        }
    }

    public sendEvent <K extends keyof S>(type: K, event: IStoreEvent<S[K]>) {
        this.rootStore.sendEvent(type , event);
    }

    public getJssdk = (clazz: string, name: string ): (params: any) => Promise<any>  => {
        if (this.renderSide === 'client') {
            return (params: any): Promise<any> => {
                 // tslint:disable-next-line:no-shadowed-variable
                 return new Promise<any>((resolve, reject) => {
                        const ucapi = (window as any).ucapi as IUcApi;
                        if (!ucapi) {
                            reject(`ucapi undefined`);
                        }
                        if (ucapi[clazz] && ucapi[clazz][name]) {
                            ucapi[clazz][name](Object.assign(params, {
                                success: (result) => {
                                    resolve(result);
                                },
                                fail: (error) => {
                                    reject(error);
                                },
                            }));
                        } else {
                            reject(`${clazz}.${name} not found`);
                        }
                 });
            };
        } else {
            throw new CustomError('server side no jssdk', {});
        }
    }

    protected async getJson<DATA_TYPE>(url: string, queries?: { [key: string]: any }): Promise<DATA_TYPE> {
        if (this.checkDisableAjax()) {
            throw new CustomError('disable ajax', {});
        }
        const response = await Axios.get<DATA_TYPE>(url, {
            params: queries,
        });
        if (response.status >= 200 && response.status < 400) {
            return response.data;
        } else {
            throw new CustomError('response error', response);
        }
    }

    protected async postJson<DATA_TYPE>(url: string, body?: any, queries?: { [key: string]: any }): Promise<DATA_TYPE> {
        if (this.checkDisableAjax()) {
            throw new CustomError('disable ajax', {});
        }
        const response = await Axios.post<DATA_TYPE>(url, body, {
            params: queries,
        });
        if (response.status >= 200 && response.status < 400) {
            return response.data;
        } else {
            throw new CustomError('response error', response);
        }
    }

    protected async getJsonWithCommonParams<DATA_TYPE>(url: string, queries?: { [key: string]: any }) {
        if (this.checkDisableAjax()) {
            throw new CustomError('disable ajax', {});
        }
        const commonParams = commonParamBuilder.getParams(queryString.parse(window.location.search));
        return await this.getJson<DATA_TYPE>(url, Object.assign(commonParams, queries || {}));
    }

    protected async postJsonWithCommonParams<DATA_TYPE>(url: string, body?: any, queries?: { [key: string]: any }) {
        if (this.checkDisableAjax()) {
            throw new CustomError('disable ajax', {});
        }
        const commonParams = commonParamBuilder.getParams(queryString.parse(window.location.search));
        return await this.postJson<DATA_TYPE>(url, {
            _csrf: cookie.get('csrfToken'),
            data: body,
        }, Object.assign(commonParams, queries || {}));
    }

    private checkDisableAjax() {
        return this.rootStore.getRootStoreConfig().renderSide === 'server';
    }

}

export interface IRootStoreConfig {
    renderSide: 'server' | 'client';
}

export default class RootStore<S = any> implements IStore<{ [name: string]: any }> {

    private config: IRootStoreConfig;

    private stores: Map<string, IStore<any>>;

    private eventHandlers: Map<string, Array<IStoreEventHandler<S[keyof S]>>>;

    constructor(config: IRootStoreConfig) {
        this.stores = new Map<string, IStore<any>>();
        this.eventHandlers = new Map<string, Array<IStoreEventHandler<S[keyof S]>>>();
        this.config = config;
    }

    public init = <T>(config: IStoreConfig<T> | IFactoryStoreConfig<T>) => {
        if ('name' in config) {
            const StoreClazz: StoreClass<T> = STORE_CLASS.get(config.name);
            const storeInstance = new StoreClazz(this, config.config);
            this.stores.set(config.name, storeInstance);
        } else if ('clazz' in config) {
            const StoreClazz: StoreClass<T> = config.clazz;
            const name = StoreClazz[Symbol.for('store:name')];
            const storeInstance = new StoreClazz(this, config.config);
            this.stores.set(name, storeInstance);
        }
    }

    public getRootStoreConfig(): IRootStoreConfig {
        return this.config;
    }

    public getInitConfig(): { [name: string]: any } {
        const result = {};
        const stores = this.stores;
        stores.forEach((value, key) => {
            result[key] = value.getInitConfig();
        });
        return result;
    }

    public getStore = (name: string): IStore<any> => {
        return this.stores.get(name);
    }

    public getStores = (): Map<string, IStore<any>> => {
        return this.stores;
    }

    public sendEvent<T extends keyof S>(type: T, event: IStoreEvent<S[T]>) {
        if (this.config.renderSide === 'server') {
            return;
        }
        const eventType = type as string;
        if (this.eventHandlers.has(eventType)) {
            this.eventHandlers.get(eventType).forEach((handler) => {
                handler(event);
            });
        }
    }

    public addEventHandler<T extends keyof S>(type: T, handler: IStoreEventHandler<S[T]>) {
        if (this.config.renderSide === 'server') {
            return;
        }
        const eventType = type as string;
        if (this.eventHandlers.has(eventType)) {
            this.eventHandlers.get(eventType).push(handler);
        } else {
            this.eventHandlers.set(eventType, [handler]);
        }
    }
}
